class CartNotification extends HTMLElement {
	constructor() {
		super();

		this.notification = document.getElementById('cart-notification');
		this.header = document.querySelector('sticky-header');
		this.onBodyClick = this.handleBodyClick.bind(this);
		this.previousCartItemKeys = new Set();
		this.previousItemCount = 0;

		this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
		this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
			closeButton.addEventListener('click', this.close.bind(this)),
		);
	}

	open() {
		this.notification.classList.add('animate', 'active');

		this.notification.addEventListener(
			'transitionend',
			() => {
				this.notification.focus();
				trapFocus(this.notification);
			},
			{ once: true },
		);

		document.body.addEventListener('click', this.onBodyClick);
	}

	close() {
		this.notification.classList.remove('active');
		document.body.removeEventListener('click', this.onBodyClick);

		removeTrapFocus(this.activeElement);
	}

	renderContents(parsedState, quantitiesAdded = {}) {
		this.cartItemKeys = [];
		this.quantitiesAdded = quantitiesAdded;

		if (parsedState.items && parsedState.items.length > 0) {
			const regularItems = parsedState.items.filter(
				(item) => item.properties?._club_shipping !== 'true',
			);
			const currentItemCount = regularItems.length;
			const itemsAdded = currentItemCount - this.previousItemCount;

			const currentKeys = new Set(regularItems.map((item) => item.key));

			if (parsedState.key && !this.previousCartItemKeys.has(parsedState.key)) {
				const isShippingFee = parsedState.items.find(
					(item) => item.key === parsedState.key && item.properties?._club_shipping === 'true',
				);
				if (!isShippingFee) {
					this.cartItemKeys.push(parsedState.key);
				}
			} else if (itemsAdded > 0 && this.previousItemCount > 0) {
				regularItems.forEach((item) => {
					if (!this.previousCartItemKeys.has(item.key)) {
						this.cartItemKeys.push(item.key);
					}
				});

				if (this.cartItemKeys.length === 0) {
					const itemsToShow = Math.min(itemsAdded, 3);
					this.cartItemKeys = regularItems.slice(-itemsToShow).map((item) => item.key);
				}
			} else {
				const itemsToShow = Math.min(regularItems.length, 3);
				this.cartItemKeys = regularItems.slice(-itemsToShow).map((item) => item.key);
			}

			this.previousCartItemKeys = currentKeys;
			this.previousItemCount = currentItemCount;
		} else if (parsedState.key) {
			const isShippingFee = parsedState.items?.find(
				(item) => item.key === parsedState.key && item.properties?._club_shipping === 'true',
			);
			if (!isShippingFee) {
				this.cartItemKeys = [parsedState.key];
				this.previousCartItemKeys.add(parsedState.key);
				this.previousItemCount = 1;
			}
		}

		this.getSectionsToRender().forEach((section) => {
			const sectionElement = document.getElementById(section.id);
			if (!sectionElement) return;

			const innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector,
			);

			if (innerHTML !== null) {
				sectionElement.innerHTML = innerHTML;
			}
		});

		if (this.header) this.header.reveal();
		this.open();
	}

	getSectionsToRender() {
		const selectors =
			this.cartItemKeys && this.cartItemKeys.length > 0
				? this.cartItemKeys.map((key) => `[id="cart-notification-product-${key}"]`).join(', ')
				: null;

		return [
			{
				id: 'cart-notification-product',
				selector: selectors,
			},
			{
				id: 'cart-notification-button',
			},
			{
				id: 'cart-icon-bubble',
			},
		];
	}

	getSectionInnerHTML(html, selector = '.shopify-section') {
		const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
		let cartItems = [];

		if (!selector) {
			cartItems = Array.from(parsedDoc.querySelectorAll('.cart-item'));
		} else if (selector.includes('cart-notification-product-')) {
			cartItems = Array.from(parsedDoc.querySelectorAll(selector));
			if (cartItems.length === 0) {
				cartItems = Array.from(parsedDoc.querySelectorAll('.cart-item'));
			}
		} else {
			const section = parsedDoc.querySelector('.shopify-section');
			return section ? section.innerHTML : null;
		}

		if (cartItems.length === 0) {
			const section = parsedDoc.querySelector('.shopify-section');
			return section ? section.innerHTML : null;
		}

		const productsMap = new Map();

		cartItems.forEach((itemElement) => {
			const productName = itemElement
				.querySelector('.cart-notification-product__name')
				?.textContent?.trim();
			const productImageContainer = itemElement.querySelector('.cart-notification-product__image');
			const productImageEl = productImageContainer?.querySelector('img');
			const productImage = productImageEl ? productImageEl.outerHTML : null;
			const productImageStyle = productImageContainer?.getAttribute('style') || '';
			const vendor = itemElement.querySelector('.caption-with-letter-spacing')?.textContent?.trim();

			// Récupérer le variant ID pour trouver la bonne quantité ajoutée
			const variantId = itemElement.dataset.variantId;

			// Récupérer le variant info et remplacer la quantité totale par la quantité ajoutée
			let variantInfo = itemElement
				.querySelector('.cart-notification-product__variant-info')
				?.textContent?.trim();

			// Remplacer la quantité du panier par la quantité ajoutée pour ce variant
			if (variantInfo && variantId && this.quantitiesAdded[variantId]) {
				variantInfo = variantInfo.replace(/^\d+/, this.quantitiesAdded[variantId]);
			}

			const productKey = productName || 'product';

			if (!productsMap.has(productKey)) {
				productsMap.set(productKey, {
					name: productName,
					image: productImage,
					imageStyle: productImageStyle,
					vendor: vendor,
					variants: [],
				});
			}

			if (variantInfo) {
				productsMap.get(productKey).variants.push({
					info: variantInfo,
				});
			}
		});

		const groupedHTML = Array.from(productsMap.values())
			.map((product) => {
				const imageHTML = product.image
					? `<div class="cart-notification-product__image global-media-settings"${product.imageStyle ? ` style="${product.imageStyle}"` : ''}>${product.image}</div>`
					: '';

				const vendorHTML = product.vendor
					? `<p class="caption-with-letter-spacing light">${product.vendor}</p>`
					: '';

				const nameHTML = product.name
					? `<h3 class="cart-notification-product__name h4">${product.name}</h3>`
					: '';

				const variantsHTML =
					product.variants.length > 0
						? `<ul class="cart-notification-product__variants">
							${product.variants
								.map(
									(variant) =>
										`<li class="cart-notification-product__variant">${variant.info}</li>`,
								)
								.join('')}
						</ul>`
						: '';

				return `<div class="cart-item cart-item--grouped">
					${imageHTML}
					<div class="cart-notification-product__content">
						${vendorHTML}
						${nameHTML}
						${variantsHTML}
					</div>
				</div>`;
			})
			.join('');

		return groupedHTML;
	}

	handleBodyClick(evt) {
		const target = evt.target;
		if (target !== this.notification && !target.closest('cart-notification')) {
			const disclosure = target.closest('details-disclosure, header-menu');
			this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
			this.close();
		}
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define('cart-notification', CartNotification);
